// Copyright (c) StrangeBee 2023

import useValidateForm from '@Hooks/useValidateForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import React, { ReactElement, useContext } from 'react';
import { ReportTemplateInput, ReportTemplateOutput } from 'thehive-sdk';
import ReportTemplateForm, { ReportTemplateFormValues } from '../ReportTemplateForm';
import { useLocalMessage } from '@Helpers/index';
import { LanguageSwitcherContext } from '@Lang/LanguageSwitcherContext';
import client from '@Client';
import { useGenericQuery } from '@Queries/genericQueries';

interface ReportTemplateCreateProps {
    onCreate?: (reportTemplate: ReportTemplateInput) => Promise<ReportTemplateOutput | void>;
    onEdit?: (reportTemplate: ReportTemplateInput, reportTemplateId: string) => Promise<ReportTemplateOutput | void>;
    defaultValues?: ReportTemplateOutput;
    triggerElem?: ReactElement;
}

export const ReportTemplateCreate = ({ onCreate, onEdit, defaultValues, triggerElem }: ReportTemplateCreateProps) => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange } = useValidateForm({ isValidInit: !!defaultValues });

    const { language } = useContext(LanguageSwitcherContext);

    const { data: fieldsList = {} } = useGenericQuery<{ [key: string]: any[] }>({
        fetchCallback: () => client.ReportTemplateController.getFieldsList(),
        key: ['get-report-fields-list'],
        manual: true,
        fetchOnStart: true,
    });

    const formatIntl = () => {
        const intl = {
            lang: language,
            observable: {} as { [key: string]: string },
            task: {} as { [key: string]: string },
            log: {} as { [key: string]: string },
            ttp: {} as { [key: string]: string },
            alert: {} as { [key: string]: string },
        };
        for (const key in fieldsList) {
            switch (key) {
                case 'alertFields':
                    for (const value of fieldsList[key]) {
                        ['_createdAt', '_createdBy', '_updatedAt', '_updatedBy'].includes(value)
                            ? (intl.alert[value] = i(`report-template.intl.${value}`))
                            : (intl.alert[value] = i(`report-template.intl.alert.${value}`));
                    }
                    break;
                case 'observableFields':
                    for (const value of fieldsList[key]) {
                        ['_createdAt', '_createdBy', '_updatedAt', '_updatedBy'].includes(value)
                            ? (intl.observable[value] = i(`report-template.intl.${value}`))
                            : (intl.observable[value] = i(`report-template.intl.observable.${value}`));
                    }
                    break;
                case 'taskFields':
                    for (const value of fieldsList[key]) {
                        ['_createdAt', '_createdBy', '_updatedAt', '_updatedBy'].includes(value)
                            ? (intl.task[value] = i(`report-template.intl.${value}`))
                            : (intl.task[value] = i(`report-template.intl.task.${value}`));
                    }
                    break;
                case 'ttpFields':
                    for (const value of fieldsList[key]) {
                        ['_createdAt', '_createdBy', '_updatedAt', '_updatedBy'].includes(value)
                            ? (intl.ttp[value] = i(`report-template.intl.${value}`))
                            : (intl.ttp[value] = i(`report-template.intl.ttp.${value}`));
                    }
                    break;
                case 'logFields':
                    for (const value of fieldsList[key]) {
                        ['_createdAt', '_createdBy', '_updatedAt', '_updatedBy'].includes(value)
                            ? (intl.log[value] = i(`report-template.intl.${value}`))
                            : (intl.log[value] = i(`report-template.intl.log.${value}`));
                    }
                    break;
                default:
                    break;
            }
        }
        return intl;
    };

    const onFinish = ({ title, description }: ReportTemplateFormValues) => {
        if (defaultValues) {
            onEdit &&
                onEdit(
                    {
                        ...defaultValues,
                        title: title?.trim(),
                        description: description?.trim(),
                    },
                    defaultValues._id,
                );
        } else {
            onCreate &&
                onCreate({
                    title: title?.trim(),
                    description: description?.trim(),
                    definition: {
                        i18n: formatIntl(),
                    },
                });
        }
    };

    return (
        <THDrawer
            title={defaultValues ? i('report-template.report-list.edit') : i('report-template.report-list.create')}
            className="drawer-form"
            triggerElem={triggerElem}
        >
            {(toggleDrawer) => (
                <ReportTemplateForm
                    form={form}
                    onFinish={(values) => {
                        toggleDrawer();
                        onFinish(values);
                        form.resetFields();
                    }}
                    onCancel={() => {
                        toggleDrawer();
                        form.resetFields();
                    }}
                    onChange={onFormValuesChange}
                    isValid={isValid}
                    defaultValues={defaultValues}
                />
            )}
        </THDrawer>
    );
};
